import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import { SectionHeading } from "@/components/shared";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { FAQS } from "@/data/content";

export default function Faq() {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQS.map((cat) => ({
      ...cat,
      items: cat.items.filter((f) => !q || f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)),
    })).filter((cat) => cat.items.length);
  }, [query]);

  return (
    <>
      <PageHero
        eyebrow="FAQ & Knowledge Base"
        lines={[<>Everything you're</>, <span className="font-serif italic font-medium text-brand">wondering, answered</span>]}
        desc="Admissions, exams, transcripts, tuition — the complete guide. Can't find your answer? Our team replies within one business day."
        testid="faq-page-heading"
      />

      <section className="container-x py-20 sm:py-28" data-testid="faq-page-section">
        <Reveal>
          <div className="relative mx-auto max-w-xl">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search answers — try “exam” or “refund”"
              data-testid="faq-search-input"
              className="field !pl-11"
            />
          </div>
        </Reveal>

        {filtered.length === 0 ? (
          <div className="mt-16 rounded-2xl border border-dashed border-slate-300 p-16 text-center" data-testid="faq-empty-state">
            <p className="font-display text-lg font-bold text-ink">No answers match that search</p>
            <p className="mt-2 text-sm text-slate-500">Ask us directly and we will respond within one business day.</p>
          </div>
        ) : (
          <div className="mx-auto mt-12 max-w-3xl space-y-12">
            {filtered.map((cat, ci) => (
              <div key={cat.category} data-testid={`faq-category-${ci}`}>
                <SectionHeading eyebrow={`0${ci + 1}`} title={cat.category} />
                <Reveal delay={0.08} className="mt-6">
                  <Accordion type="single" collapsible className="card-line bg-white px-6" data-testid={`faq-accordion-${ci}`}>
                    {cat.items.map((f, i) => (
                      <AccordionItem key={i} value={`${ci}-${i}`}>
                        <AccordionTrigger className="text-left font-display text-base font-semibold text-ink" data-testid={`faq-item-${ci}-${i}`}>
                          {f.q}
                        </AccordionTrigger>
                        <AccordionContent className="text-sm leading-relaxed text-slate-600">
                          {f.a}
                          {f.link && (
                            <>
                              {" "}
                              <Link to={f.link.to} className="font-semibold text-brand underline underline-offset-2 hover:text-ink" data-testid="faq-refund-policy-link">
                                {f.link.label}
                              </Link>
                            </>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </Reveal>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="container-x pb-24" data-testid="faq-cta-section">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-ink px-8 py-14 text-center sm:px-16">
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-brandlight">Still have questions?</p>
            <h2 className="mx-auto mt-4 max-w-2xl font-display text-2xl font-extrabold tracking-tight text-ivory sm:text-3xl">
              Ask us <span className="font-serif italic font-medium text-brandlight">anything.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-ivory/70 sm:text-base">
              Didn't find your answer above? Contact us with any additional queries — our team replies within one business day.
            </p>
            <div className="mt-8">
              <Link to="/contact" data-testid="faq-cta-contact" className="btn-accent">
                Contact <span className="text-ink">us</span>
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
